/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';

import {
    FormControl,
    InputLabel,
    FormHelperText,
    Select as MuiSelect,
    SelectProps,
    MenuItem,
    InputAdornment,
    SelectChangeEvent,
    ListItemIcon,
    ListItemText,
    Divider,
    Typography
} from '@mui/material';

import { IMenuItem } from '@dw/models/shared';
import { useFormContext } from './Form.Context';
import { ProgressSpinner } from '../ProgressSpinner';
import { camelCaseToUserText, componentClassName } from '@dw/utils';
import { Check } from '@mui/icons-material';
import { HStack } from '../HStack';

// Sentinel value for the synthetic "All" option (see multipleAllText). Real option values are never this.
const ALL_OPTION_VALUE = '__ALL__';

export interface IFormSelectInputProps<T> extends Omit<SelectProps, 'label' | 'name'> {
    name: keyof T;
    options: IMenuItem[];
    label?: string;

    startIcon?: React.ReactNode;
    isLoading?: boolean;
    helperText?: React.ReactNode;
    multipleCountOnly?: boolean;
    /** For a multiple + multipleCountOnly select: text shown when nothing is selected (e.g. "All Classes"). */
    multipleAllText?: string;
}
export function FormSelectInput<T>(props: IFormSelectInputProps<T>) {

    const {
        label,
        name,
        options,
        startIcon,
        isLoading,
        sx,
        helperText,
        multipleCountOnly,
        multipleAllText,
        ...baseInputProps
    } = props;

    const {
        formData,
        handleFormChange,
        disabled: formDisabled,
        formErrors,
        customFormErrors,
        submitAttempted
    } = useFormContext<T>();

    const [hasFocus, setHasFocus] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    // When set, the multiple select gets a synthetic exclusive "All" option (an empty selection = all).
    const useAllOption = !!multipleAllText && !!baseInputProps.multiple;

    const handleSelectionChange = (e: SelectChangeEvent<unknown>) => {
        const newValue = e.target.value;
        if (useAllOption && Array.isArray(newValue) && newValue.includes(ALL_OPTION_VALUE)) {
            // "All" chosen → clear to an empty selection (= all) and close the menu; it's exclusive of specific picks.
            handleFormChange(name, []);
            setMenuOpen(false);
            return;
        }
        handleFormChange(name, newValue);
    };

    const getIcon = (icon: React.ReactNode) => {
        const color = props.error ? 'error' : '';
        return React.cloneElement(icon as any, {
            color
        });
    };

    const buildStartIcon = () => !startIcon ? null : (
        <InputAdornment position='start'>
            {getIcon(startIcon)}
        </InputAdornment>
    );

    const buildEndIcon = () => !isLoading ? null : (
        <InputAdornment position='end'>
            <ProgressSpinner size={20} />
        </InputAdornment>
    );

    const cn = componentClassName('form-select-input');

    return (
        <FormControl
            fullWidth
            error={submitAttempted && (formErrors[name] || !!customFormErrors[name])}
            required={props.required}
            margin={props.margin}
            size={props.size}
            sx={sx}
            className={cn()}
        >
            <InputLabel
                id={String(name)}
                size={props.size === 'small' ? 'small' : 'normal'}
                shrink={(baseInputProps.multiple ? (((formData[name] ?? []) as any[]).length > 0 || !!multipleAllText) : !!formData[name]) || hasFocus || !!startIcon}
            >
                {label ?? camelCaseToUserText(String(name))}
            </InputLabel>
            <MuiSelect
                {...baseInputProps}
                labelId={String(name)}
                // MUI requires an array `value` for `multiple` and throws if it's ever undefined for
                // even a single render - which is exactly what happens when a parent adds a new row
                // and its multi-select in the same batched update (the new field isn't in formData
                // yet). A non-multiple select has the same "uncontrolled -> controlled" trap for an
                // undefined value, so it gets the same treatment, matching the coalescing this file
                // already does at the InputLabel `shrink` and "Check" icon checks below.
                value={(baseInputProps.multiple ? (formData[name] ?? []) : (formData[name] ?? '')) as any}
                label={label ?? camelCaseToUserText(String(name))}
                onChange={handleSelectionChange}
                error={submitAttempted && (formErrors[name] || !!customFormErrors[name])}
                disabled={props.disabled || formDisabled || isLoading}
                name={String(name)}
                startAdornment={buildStartIcon()}
                endAdornment={buildEndIcon()}
                size={props.size}
                {...(useAllOption ? {
                    open: menuOpen,
                    onOpen: () => setMenuOpen(true),
                    onClose: () => setMenuOpen(false),
                    displayEmpty: true,
                } : {})}
                renderValue={(selected) => {
                    if (Array.isArray(selected)) {
                        if (multipleCountOnly) {
                            if (selected.length === 0 && multipleAllText) {
                                return multipleAllText;
                            }
                            return `${selected.length} selected`;
                        } else {
                            const matchingItems = selected
                                .map(x => options.find(y => x === (y.value ?? y.text)))
                                .map(x => x?.text);
                            return matchingItems.join(', ');
                        }
                    } else {
                        const matchingItem = options!.find(x => (x.value ?? x.text) === selected)
                        return matchingItem?.text ?? matchingItem?.content;
                    }
                }}
                onFocus={() => setHasFocus(true)}
                onBlur={() => setHasFocus(false)}
            >
                {useAllOption && (
                    <MenuItem value={ALL_OPTION_VALUE} key='__all_option__'>
                        <HStack breakpoint={false} flex={1}>
                            <HStack columnGap={1} breakpoint={false} flex={1}>
                                <ListItemText>{multipleAllText}</ListItemText>
                            </HStack>
                            {((formData[name] ?? []) as any[]).length === 0 && (
                                <ListItemIcon>
                                    <Check color='primary' />
                                </ListItemIcon>
                            )}
                        </HStack>
                    </MenuItem>
                )}
                {
                    options!.filter(x => !x.hidden).map((option, index) => option.isDivider ? (
                        <Divider key={index} />
                    ) : (
                        <MenuItem
                            value={option.value ?? option.text}
                            key={option.value ?? option.text ?? index}
                            disabled={option.disabled}
                        >
                            <HStack breakpoint={false} flex={1}>
                                <HStack columnGap={1} breakpoint={false} flex={1}>
                                    {
                                        option.icon && (
                                            <ListItemIcon>
                                                {option.icon}
                                            </ListItemIcon>
                                        )
                                    }
                                    {option.content ?? (
                                        <ListItemText>{option.text ?? camelCaseToUserText(option.value ?? '')}</ListItemText>
                                    )}
                                </HStack>
                                
                                {baseInputProps.multiple && ((formData[name] ?? []) as any[]).includes(option.value ?? option.text) && (
                                    <ListItemIcon>
                                        <Check color='primary' />
                                    </ListItemIcon>
                                )}
                            </HStack>
                        </MenuItem>
                    ))
                }
            </MuiSelect>

            {submitAttempted && !!customFormErrors[name] ? (
                <FormHelperText>
                    <Typography variant='caption' color='error'>{customFormErrors[name]}</Typography>
                </FormHelperText>
            ) : helperText ? (
                <FormHelperText>
                    {helperText}
                </FormHelperText>
            ) : null}
        </FormControl>
    )
};
